import { Download } from 'lucide-react';

interface DocumentPreviewProps {
  contentType: string;
  fileName: string;
  fileUrl: string;
}

/**
 * Inline preview for an uploaded document. Switches by MIME type:
 *  - `image/*` → native `<img>`
 *  - `application/pdf` → native `<iframe>` (browser PDF viewer / iOS QuickLook)
 *  - anything else → a download link
 *
 * `fileUrl` must be a presigned, time-limited URL. We reuse the same one the
 * sheet already has from `UploadedDocumentDto.current_revision.file_url`.
 */
export function DocumentPreview({ contentType, fileName, fileUrl }: DocumentPreviewProps) {
  const type = contentType.toLowerCase();

  if (type.startsWith('image/')) {
    return (
      <img src={fileUrl} alt={fileName} className="max-h-[70vh] w-full rounded-md border bg-muted/30 object-contain" />
    );
  }

  if (type === 'application/pdf') {
    // `sandbox` is defense-in-depth: the URL is a presigned S3 GET on our
    // own bucket, but if anything ever redirects the iframe elsewhere we
    // want it limited. The two tokens are the minimum the built-in browser
    // PDF viewer needs to render and respond to scroll/zoom.
    return (
      <iframe
        title={fileName}
        src={fileUrl}
        sandbox="allow-scripts allow-same-origin"
        className="h-[70vh] w-full rounded-md border bg-muted/30"
      />
    );
  }

  return (
    <a
      href={fileUrl}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-center gap-2 rounded-md border border-dashed p-6 text-sm text-muted-foreground hover:bg-accent">
      <Download className="h-4 w-4" />
      Preview not available — download {fileName}
    </a>
  );
}
